import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Divider, Stack, Typography, useTheme } from '@mui/material';
import {
  AddRounded,
  CheckCircleRounded,
  CloudDoneRounded,
  CloudOffRounded,
  DeleteOutlineRounded,
  DownloadRounded,
  ErrorOutlineRounded,
  Inventory2Outlined,
  LockClockRounded,
  SyncRounded,
} from '@mui/icons-material';
import chroma from 'chroma-js';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import GlassButton from '../components/GlassButton';
import ModalTemplate from '../components/common/ModalTemplate';
import Input from '../components/Input';
import Select from '../components/Select';
import Checkbox from '../components/Checkbox';
import DatePickerUTC from '../components/DatePickerUTC';
import { getEngine, currentIdentity } from './runtime';
import { isOfflineAddonActive } from './addonFlag';
import { emptyState, pending, recordsFor } from './engine';
import { translate } from './messages';

const labels = {
  queued: 'Enregistré localement',
  pending: 'En cours de synchronisation',
  applied: 'Synchronisé',
  synced: 'Synchronisé',
  conflict: 'Conflit à résoudre',
  rejected: 'Publication refusée',
};

const errorLabel = (code) => ({
  OFFLINE_NOT_ENABLED: 'Le mode hors ligne doit être activé pour ce compte et cette structure.',
  SELECTION_TOO_LARGE: 'Cette sélection dépasse 1 000 fiches. Préparez un dossier plus petit.',
  OFFLINE_ACCESS_EXPIRED: 'Reconnectez-vous pour renouveler votre accès de sept jours. Votre travail est conservé.',
  ACTION_ONLINE_ONLY: 'Cette action est disponible en ligne seulement.',
  FIELD_ONLINE_ONLY: 'Ce champ est disponible en ligne seulement.',
  REQUIRED_FIELD: 'Complétez les champs obligatoires.',
  STRUCTURE_CHANGED: 'Le formulaire a changé. Actualisez la sélection puis corrigez la fiche.',
  ACCESS_REVOKED: 'Vos droits ont changé. Reconnectez-vous. Vos changements locaux sont conservés.',
  AUTHENTICATION_REQUIRED: 'Une reconnexion est nécessaire pour publier. Votre travail est conservé.',
  UNPUBLISHED_CHANGES: 'Cette sélection contient des changements non publiés.',
  SYNC_REQUIRED_BEFORE_REMOVAL: 'Vérifiez le résultat des publications en cours avant de retirer cette sélection.',
  NETWORK_UNAVAILABLE: 'Connexion indisponible. Le travail local reste disponible.',
  INVALID_NUMBER: 'Entrez un nombre valide.',
  INVALID_DATE: 'Entrez une date valide.',
  INVALID_SELECTION: 'Choisissez une valeur autorisée.',
  ACCOUNT_CHANGED: 'Le compte actif a changé. Rouvrez le bon compte pour publier.',
  OFFLINE_BROWSER_UNSUPPORTED: 'Ce navigateur ne permet pas de préparer le mode hors ligne.',
  OFFLINE_SHELL_NOT_READY: 'Rechargez Node en ligne avant de préparer cette version.',
})[code] || code || 'Une erreur est survenue.';

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');
const formatSize = (value) => `${Math.max(1, Math.ceil(value / 1024))} Ko`;

function fieldLabel(field, language) {
  return language?.startsWith('en') ? field.label_en || field.label || field.key : field.label || field.label_en || field.key;
}

function statusTone(status, theme, mainColor) {
  const dark = theme.palette.mode === 'dark';
  if (status === 'synced' || status === 'applied') return {
    icon: <CheckCircleRounded fontSize="small" />,
    color: '#15803d',
    bg: dark ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.08)',
    border: dark ? 'rgba(74,222,128,0.22)' : 'rgba(22,163,74,0.18)',
  };
  if (status === 'conflict' || status === 'rejected') return {
    icon: <ErrorOutlineRounded fontSize="small" />,
    color: '#b42318',
    bg: dark ? 'rgba(248,113,113,0.12)' : 'rgba(220,38,38,0.08)',
    border: dark ? 'rgba(248,113,113,0.25)' : 'rgba(220,38,38,0.18)',
  };
  if (status === 'pending') return {
    icon: <SyncRounded fontSize="small" />,
    color: mainColor,
    bg: chroma(mainColor).alpha(dark ? 0.14 : 0.08).css(),
    border: chroma(mainColor).alpha(dark ? 0.28 : 0.18).css(),
  };
  return {
    icon: <CloudOffRounded fontSize="small" />,
    color: dark ? '#facc15' : '#92400e',
    bg: dark ? 'rgba(250,204,21,0.1)' : 'rgba(245,158,11,0.1)',
    border: dark ? 'rgba(250,204,21,0.22)' : 'rgba(245,158,11,0.22)',
  };
}

function Surface({ children, sx }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)'}`,
        background: dark ? 'rgba(22,25,31,0.92)' : 'rgba(255,255,255,0.94)',
        boxShadow: dark ? '0 18px 50px rgba(0,0,0,0.28)' : '0 18px 42px rgba(15,23,42,0.08)',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function Stat({ label, value }) {
  return (
    <Box sx={{ minWidth: 118 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

function StatusChip({ status, label }) {
  const theme = useTheme();
  const businessData = useSelector((state) => state.core.businessData);
  const mainColor = businessData?.mainColor || theme.palette.primary.main;
  const tone = statusTone(status, theme, mainColor);
  return (
    <Chip
      icon={tone.icon}
      label={label}
      size="small"
      sx={{
        height: 28,
        borderRadius: '9999px',
        bgcolor: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.color,
        fontWeight: 700,
        '& .MuiChip-icon': { color: tone.color, ml: 1 },
      }}
    />
  );
}

function Editor({ pack, record, resolving, engine, selectionKey, allRecords, onSaved, onCancel }) {
  const { i18n } = useTranslation();
  const tr = (text) => translate(text, i18n.language);
  const [values, setValues] = useState(() => ({
    ...(record?.values || {}),
    ...(resolving?.op.changes || {}),
  }));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fields = pack.manifest.capabilities.fields;
  const canEdit = record ? pack.manifest.capabilities.update && !record.locked : pack.manifest.capabilities.create;
  const server = resolving?.error?.details?.record;
  const conflictKeys = resolving?.error?.details?.keys || [];

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const editable = Object.fromEntries(
        fields
          .filter((field) => field.writable && values[field.key] !== undefined)
          .map((field) => [field.key, values[field.key]])
      );
      if (resolving) {
        await engine.resolve(resolving.op.id, editable);
      } else {
        await engine.save(selectionKey, record?.path || null, editable);
      }
      onSaved();
      engine.sync().catch(() => {});
    } catch (err) {
      setError(errorLabel(err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Surface sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {resolving ? tr('Résoudre la publication') : record ? tr('Modifier la fiche') : tr('Nouvelle fiche')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canEdit
              ? tr('Les changements sont enregistrés sur cet appareil avant publication.')
              : tr('Cette fiche est disponible en lecture seule.')}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gap: 2 }}>
          {fields.map((field) => {
            const value = values[field.key];
            const label = fieldLabel(field, i18n.language);
            const change = (next) => setValues((current) => ({ ...current, [field.key]: next }));
            const readonly = !canEdit || !field.writable;

            return (
              <Box
                key={field.key}
                sx={{
                  display: 'grid',
                  gap: 1,
                  p: readonly || conflictKeys.includes(field.key) ? 1.5 : 0,
                  borderRadius: readonly || conflictKeys.includes(field.key) ? '10px' : 0,
                  border: conflictKeys.includes(field.key) ? '1px solid rgba(220,38,38,0.22)' : '1px solid transparent',
                  bgcolor: conflictKeys.includes(field.key) ? 'rgba(220,38,38,0.04)' : 'transparent',
                }}
              >
                {readonly ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{String(value ?? '-')}</Typography>
                    <Typography variant="caption" color="text.secondary">{tr('Lecture seule')}</Typography>
                  </Box>
                ) : field.type === 'boolean' ? (
                  <Checkbox label={label} value={value === true} onChange={(event) => change(event.target.checked)} />
                ) : field.type === 'reference' ? (
                  <Select
                    label={tr('Dossier parent')}
                    value={value || ''}
                    selections={allRecords
                      .filter((item) => item.path !== record?.path)
                      .map((item) => ({ value: item.path, label: item.values.name || item.path }))}
                    onChange={(_, next) => change(next || null)}
                  />
                ) : ['date', 'date-time'].includes(field.type) ? (
                  <DatePickerUTC
                    label={label}
                    value={value}
                    timePicker={field.type === 'date-time'}
                    onChange={(next, type, date) => change(next === null ? null : new Date(date).toISOString())}
                    clearable={!field.required}
                  />
                ) : field.selections?.length ? (
                  <Select label={label} value={value} selections={field.selections} onChange={(_, next) => change(next)} />
                ) : (
                  <Input
                    label={label}
                    required={field.required}
                    value={value ?? ''}
                    type={field.type === 'date' ? 'date' : field.type === 'date-time' ? 'datetime-local' : 'text'}
                    help={['number', 'integer'].includes(field.type) ? tr('Nombre') : undefined}
                    onChange={(event) => {
                      const next = event.target.value;
                      change(['number', 'integer'].includes(field.type) ? (next === '' ? null : Number(next)) : next);
                    }}
                  />
                )}

                {server && conflictKeys.includes(field.key) && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {tr('Serveur :')} {String(server.values[field.key] ?? '-')}
                    </Typography>
                    <GlassButton variant="outlined" onClick={() => change(server.values[field.key])}>
                      {tr('Utiliser la valeur du serveur')}
                    </GlassButton>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {error && (
          <Typography role="alert" color="error" sx={{ fontWeight: 600 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canEdit && (
            <GlassButton onClick={submit} disabled={saving}>
              {saving ? tr('Enregistrement...') : tr('Enregistrer sur cet appareil')}
            </GlassButton>
          )}
          <GlassButton variant="outlined" onClick={onCancel} disabled={saving}>
            {tr('Retour')}
          </GlassButton>
        </Box>
      </Stack>
    </Surface>
  );
}

export default function OfflineBoundary({ children }) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const tr = (text) => translate(text, i18n.language);
  const businessData = useSelector((state) => state.core.businessData);
  const mainColor = businessData?.mainColor || theme.palette.primary.main;
  const dark = theme.palette.mode === 'dark';

  const [visible, setVisible] = useState(() => navigator.onLine === false || window.location.pathname === '/offline');
  const [online, setOnline] = useState(navigator.onLine);
  const [identity, setIdentity] = useState(currentIdentity());
  const [addonActive, setAddonActive] = useState(isOfflineAddonActive);
  const [state, setState] = useState(emptyState);
  const [selection, setSelection] = useState('');
  const [query, setQuery] = useState('');
  const [order, setOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editor, setEditor] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);

  const engine = useMemo(() => getEngine(identity), [identity]);

  useEffect(() => {
    const updateIdentity = () => {
      const next = currentIdentity();
      setIdentity((previous) => (
        previous?.userId === next?.userId && previous?.businessId === next?.businessId ? previous : next
      ));
      setAddonActive(isOfflineAddonActive());
    };
    const unsubscribe = onAuthStateChanged(auth, updateIdentity);
    const interval = setInterval(updateIdentity, 1000);
    const handleOpen = (event) => {
      setVisible(true);
      if (event.detail?.selectionKey) setSelection(event.detail.selectionKey);
    };
    const connectivity = () => {
      const nextOnline = navigator.onLine;
      setOnline(nextOnline);
      if (!nextOnline) setVisible(true);
      updateIdentity();
    };
    window.addEventListener('node-offline-open', handleOpen);
    window.addEventListener('online', connectivity);
    window.addEventListener('offline', connectivity);
    return () => {
      unsubscribe();
      clearInterval(interval);
      window.removeEventListener('node-offline-open', handleOpen);
      window.removeEventListener('online', connectivity);
      window.removeEventListener('offline', connectivity);
    };
  }, []);

  useEffect(() => {
    let active = true;
    setEditor(null);
    setSelection('');
    setState(emptyState());
    if (!engine) return undefined;
    const refresh = () => engine.read().then((next) => {
      if (active) setState(next);
    }).catch((error) => {
      if (active) setMessage(errorLabel(error.message));
    });
    const sync = () => {
      if (navigator.onLine && document.visibilityState !== 'hidden') engine.sync().catch(() => {});
    };
    refresh();
    sync();
    const interval = setInterval(() => {
      refresh();
      sync();
    }, 15000);
    window.addEventListener('node-offline-change', refresh);
    window.addEventListener('online', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('node-offline-change', refresh);
      window.removeEventListener('online', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [engine]);

  const packageEntries = useMemo(() => Object.entries(state.packages), [state.packages]);
  const pack = state.packages[selection] || packageEntries[0]?.[1] || null;
  const activeSelection = selection || packageEntries[0]?.[0] || '';
  const expired = pack && pack.manifest.expiresAt <= Date.now();
  const records = useMemo(() => (
    pack && !expired ? recordsFor(state, activeSelection) : []
  ), [pack, expired, state, activeSelection]);
  const allRecords = packageEntries
    .filter(([, item]) => item.manifest.expiresAt > Date.now())
    .flatMap(([key]) => recordsFor(state, key));
  const work = pending(state);
  const localWork = work.filter((item) => item.status !== 'synced');
  const conflictCount = work.filter((item) => ['conflict', 'rejected'].includes(item.status)).length;
  const filteredRecords = useMemo(() => records
    .filter((record) => {
      const text = JSON.stringify(record.values || {}).toLocaleLowerCase();
      const matchesQuery = text.includes(query.toLocaleLowerCase());
      const matchesStatus = statusFilter === 'all' || record.syncStatus !== 'synced';
      return matchesQuery && matchesStatus;
    })
    .sort((a, b) => String(a.values.name || '').localeCompare(String(b.values.name || '')) * (order === 'desc' ? -1 : 1)),
  [records, query, statusFilter, order]);

  const run = async (fn) => {
    setBusy(true);
    setMessage('');
    try {
      await fn();
    } catch (error) {
      setMessage(errorLabel(error.message));
    } finally {
      setBusy(false);
    }
  };

  const refreshSelection = async () => {
    const manifest = await engine.prepare(pack.manifest.selection);
    setSelection(manifest.selectionKey);
    setMessage(tr('Sélection actualisée.'));
  };

  const exportChanges = async () => {
    const blob = new Blob([await engine.exportChanges()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'node-changements-offline.json';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const closeWorkspace = () => {
    if (editor && !window.confirm(tr('Quitter le formulaire sans enregistrer ?'))) return;
    setEditor(null);
    setVisible(false);
  };

  return (
    <>
      {children}
      {visible && addonActive && (
        <ModalTemplate
          title={tr('Travail hors ligne')}
          icon={online ? <CloudDoneRounded /> : <CloudOffRounded />}
          width="1200px"
          maxHeight="88vh"
          onClose={closeWorkspace}
        >
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
                {tr('Travaillez avec la copie locale de votre système, puis publiez automatiquement au retour du réseau.')}
              </Typography>
              <StatusChip status={online ? 'synced' : 'queued'} label={online ? tr('Connexion disponible') : tr('Hors ligne')} />
            </Box>

        {!identity ? (
          <Surface sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {tr('Première connexion requise')}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {tr('Connectez-vous une première fois et préparez vos données sur cet appareil pour utiliser le mode hors ligne.')}
            </Typography>
          </Surface>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.9fr 1fr' }, gap: 2.5 }}>
              <Stack spacing={2.5}>
                <Surface sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack spacing={2.25}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{tr('Copie locale de votre système')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tr('La copie locale reste utilisable sept jours après sa dernière validation serveur.')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Stat label={tr('Sélections')} value={packageEntries.length} />
                        <Stat label={tr('Fiches locales')} value={records.length} />
                        <Stat label={tr('À publier')} value={localWork.length} />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(248,250,252,0.9)',
                      }}
                    >
                      <Inventory2Outlined sx={{ color: mainColor }} />
                      <Box>
                        <Typography sx={{ fontWeight: 750 }}>{tr('Copie locale de votre système')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {packageEntries.length
                            ? tr('Gérez la copie hors ligne depuis les réglages du compte.')
                            : tr('Activez la copie hors ligne dans les réglages du compte pendant que vous êtes connecté.')}
                        </Typography>
                      </Box>
                    </Box>
                    {message && (
                      <Typography role="status" sx={{ color: state.lastError ? 'error.main' : 'text.secondary', fontWeight: 600 }}>
                        {message}
                      </Typography>
                    )}
                    {state.lastError && (
                      <Typography role="status" color="error" sx={{ fontWeight: 600 }}>
                        {tr('Publication en attente :')} {errorLabel(state.lastError)}
                      </Typography>
                    )}
                  </Stack>
                </Surface>

                {pack && (
                  <Surface sx={{ p: { xs: 2, md: 3 } }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {pack.manifest.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {tr('Préparé le')} {formatDate(pack.manifest.preparedAt)}. {formatSize(new Blob([JSON.stringify(pack)]).size)}.{' '}
                            {expired ? tr('Accès expiré, travail conservé') : `${tr('Valide jusqu’au')} ${formatDate(pack.manifest.expiresAt)}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <GlassButton variant="outlined" startIcon={<SyncRounded />} disabled={busy || !online} onClick={() => run(refreshSelection)}>
                            {tr('Actualiser')}
                          </GlassButton>
                          <GlassButton startIcon={<AddRounded />} disabled={expired || !pack.manifest.capabilities.create} onClick={() => setEditor({ record: null })}>
                            {tr('Nouvelle fiche')}
                          </GlassButton>
                        </Box>
                      </Box>

                      <Divider />

                      {expired ? (
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', color: 'text.secondary' }}>
                          <LockClockRounded />
                          <Typography>{errorLabel('OFFLINE_ACCESS_EXPIRED')}</Typography>
                        </Box>
                      ) : editor ? (
                        <Editor
                          key={editor.resolving?.op.id || editor.record?.path || 'create'}
                          pack={pack}
                          record={editor.record}
                          resolving={editor.resolving}
                          engine={engine}
                          selectionKey={activeSelection}
                          allRecords={allRecords}
                          onSaved={() => {
                            setEditor(null);
                            setMessage(tr('Enregistré sur cet appareil.'));
                          }}
                          onCancel={() => setEditor(null)}
                        />
                      ) : (
                        <>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 180px 210px' }, gap: 1.5 }}>
                            <Input
                              label={tr('Rechercher dans les fiches téléchargées')}
                              value={query}
                              onChange={(event) => setQuery(event.target.value)}
                            />
                            <Select
                              label={tr('Trier')}
                              value={order}
                              selections={[
                                { value: 'asc', label: tr('Nom A-Z') },
                                { value: 'desc', label: tr('Nom Z-A') },
                              ]}
                              onChange={(_, value) => setOrder(value)}
                            />
                            <Select
                              label={tr('Filtrer')}
                              value={statusFilter}
                              selections={[
                                { value: 'all', label: tr('Toutes') },
                                { value: 'local', label: tr('Changements locaux') },
                              ]}
                              onChange={(_, value) => setStatusFilter(value)}
                            />
                          </Box>

                          <Box sx={{ display: 'grid', gap: 1 }}>
                            {filteredRecords.map((record) => (
                              <Box
                                key={record.path}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                                  gap: 1.5,
                                  alignItems: 'center',
                                  p: 1.5,
                                  borderRadius: '10px',
                                  border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`,
                                  bgcolor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.88)',
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {record.values.name || tr('Sans nom')}
                                  </Typography>
                                  <Box sx={{ mt: 0.75, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <StatusChip status={record.syncStatus} label={tr(labels[record.syncStatus])} />
                                    <Typography variant="caption" color="text.secondary">
                                      {record.path}
                                    </Typography>
                                  </Box>
                                </Box>
                                <GlassButton
                                  variant={pack.manifest.capabilities.update && !record.locked ? 'contained' : 'outlined'}
                                  onClick={() => setEditor({ record })}
                                >
                                  {pack.manifest.capabilities.update && !record.locked ? tr('Modifier') : tr('Consulter')}
                                </GlassButton>
                              </Box>
                            ))}
                            {!filteredRecords.length && (
                              <Box sx={{ p: 3, borderRadius: '10px', bgcolor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.88)', textAlign: 'center' }}>
                                <Inventory2Outlined sx={{ color: 'text.secondary', mb: 1 }} />
                                <Typography sx={{ fontWeight: 700 }}>{tr('Aucune fiche à afficher')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {query ? tr('Ajustez la recherche ou les filtres.') : tr('Créez la copie hors ligne dans les réglages du compte.')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </>
                      )}
                    </Stack>
                  </Surface>
                )}
              </Stack>

              <Stack spacing={2.5}>
                <Surface sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{tr('Publications')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {localWork.length
                          ? tr('Les changements seront publiés dans l’ordre dès que la connexion répond.')
                          : tr('Aucun changement local en attente.')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <GlassButton startIcon={<SyncRounded />} disabled={busy || !online || !localWork.length} onClick={() => run(() => engine.sync())}>
                        {tr('Synchroniser')}
                      </GlassButton>
                      <GlassButton variant="outlined" startIcon={<DownloadRounded />} disabled={!localWork.length} onClick={() => run(exportChanges)}>
                        {tr('Exporter')}
                      </GlassButton>
                    </Box>
                    {conflictCount > 0 && (
                      <Typography color="error" sx={{ fontWeight: 700 }}>
                        {conflictCount} {tr('publication à corriger')}
                      </Typography>
                    )}
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {localWork.map((item) => (
                        <Box
                          key={item.op.id}
                          sx={{
                            p: 1.5,
                            borderRadius: '10px',
                            border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`,
                            bgcolor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.88)',
                          }}
                        >
                          <Typography sx={{ fontWeight: 750 }}>
                            {item.op.changes.name || item.baseRecord?.values.name || item.op.id}
                          </Typography>
                          <Box sx={{ mt: 0.75, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <StatusChip status={item.status} label={tr(labels[item.status])} />
                            {item.error && <Typography variant="body2" color="error">{errorLabel(item.error.code)}</Typography>}
                          </Box>
                          {['conflict', 'rejected'].includes(item.status) && (
                            <Box sx={{ mt: 1 }}>
                              <GlassButton
                                variant="outlined"
                                startIcon={<ErrorOutlineRounded />}
                                onClick={() => {
                                  setSelection(item.selectionKey);
                                  setEditor({
                                    record: item.error?.details?.record || item.baseRecord,
                                    resolving: item,
                                  });
                                }}
                              >
                                {tr('Examiner')}
                              </GlassButton>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                </Surface>

                <Surface sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{tr('Copies locales')}</Typography>
                    <Select
                      label={tr('Copie active')}
                      value={activeSelection}
                      selections={packageEntries.map(([key, item]) => ({
                        value: key,
                        label: `${item.manifest.name} (${item.manifest.count})`,
                      }))}
                      onChange={(_, value) => {
                        setSelection(value);
                        setEditor(null);
                        setRemoving(false);
                      }}
                    />
                    {pack && (
                      <>
                        <GlassButton variant="outlined" startIcon={<DeleteOutlineRounded />} onClick={() => setRemoving(!removing)}>
                          {tr('Retirer de cet appareil')}
                        </GlassButton>
                        {removing && (
                          <Box sx={{ display: 'grid', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {tr('Cette action abandonne les changements non publiés de cette copie.')}
                            </Typography>
                            <GlassButton
                              disabled={busy}
                              onClick={() => run(async () => {
                                await engine.removeSelection(activeSelection, true);
                                setSelection('');
                                setRemoving(false);
                              })}
                            >
                              {tr('Confirmer le retrait')}
                            </GlassButton>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                </Surface>

                <Surface sx={{ p: { xs: 2, md: 3 }, borderColor: chroma(mainColor).alpha(dark ? 0.28 : 0.18).css() }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    {online ? <CloudDoneRounded sx={{ color: mainColor }} /> : <CloudOffRounded sx={{ color: mainColor }} />}
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>
                        {online ? tr('Publication automatique active') : tr('Travail local actif')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tr('Les sauvegardes confirmées restent sur cet appareil jusqu’à publication ou abandon explicite.')}
                      </Typography>
                    </Box>
                  </Box>
                </Surface>
              </Stack>
            </Box>
          </>
        )}
          </Box>
        </ModalTemplate>
      )}
    </>
  );
}
